import { useContext, useState } from 'preact/hooks';
import { api } from 'api';
import { ApplicationContext } from 'contexts/use-application-setup';
import { sessionStore } from 'global/session.store';
import { AUTH_ERROR_TEXT, EMAIL_MFA, VIRTUAL_MFA } from 'constants/api';

type AuthProgressStatus = 'idle' | 'loading' | 'error' | 'success';
type MfaMode = typeof VIRTUAL_MFA | typeof EMAIL_MFA | null;

export const ERROR_ACCESS_DENIED_INVALID_MFA_METHOD = 'ACCESS_DENIED_INVALID_MFA_METHOD';

export const useAuth = () => {
  const { thisSiteId, accountIdCode } = useContext(ApplicationContext);
  const [authProgressStatus, setAuthProgressStatus] = useState<AuthProgressStatus>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [mfaMode, setMfaMode] = useState<MfaMode | null>(null);

  const login = async (email: string, password: string, mfaCode?: string) => {
    setAuthProgressStatus('loading');
    setErrorText(null);

    const mfaData = mfaCode
      ? {
          mfaMethod: mfaMode,
          verificationCode: mfaCode,
        }
      : undefined;

    try {
      const loginResponse = await api.post('uw/v2/auth/login-widget', {
        email,
        password,
        siteId: thisSiteId,
        account: accountIdCode,
        ...mfaData,
      });

      if (verifyAuthResponse(loginResponse)) {
        sessionStore.login({
          token: loginResponse.data.token,
          refreshToken: loginResponse.data.refreshToken,
          user: loginResponse.data.user,
          jwtUserDetails: loginResponse.data.jwtUserDetails,
        });

        setAuthProgressStatus('success');
        setErrorText(null);
      } else {
        throw new Error(AUTH_ERROR_TEXT.DEFAULT);
      }
    } catch (error) {
      // When MFA (i.e., 2FA) is enabled, server responses with a specific 400 error after a succeful email authentication. Thus we understand that MFA is enabled for this account and here we check if we have this MFA error response.
      const MfaStatus = checkIfMfaModeEnabled(error?.response);

      if (MfaStatus) {
        setMfaMode(MfaStatus);
        setAuthProgressStatus('idle');
      } else {
        // Process failed authentication (wrong password/email, server error, etc)
        setAuthProgressStatus('error');
        const nextErrorText = typeof error === 'string' ? error : getErrorText(error.response);
        setErrorText(nextErrorText);
      }
    }
  };

  const sendMfaCodeToEmail = async (email: string) => {
    try {
      setErrorText(null);
      await api.put('sso/v0/public/service-user/mfa/email/send', { email });
    } catch (error) {
      const nextErrorText = typeof error === 'string' ? error : getErrorText(error.response);
      setErrorText(nextErrorText);
    }
  };

  return {
    login,
    authProgressStatus,
    errorText,
    mfaMode,
    sendMfaCodeToEmail,
  };
};

/** Choose appropriate error text depending on a server response */
export function getErrorText(response: string): string {
  try {
    const parsed = JSON.parse(response);
    const errorType = parsed.errors[0]?.errorType;
    const errorClass = parsed.errors[0]?.errorClass;

    return AUTH_ERROR_TEXT[errorClass][errorType] || AUTH_ERROR_TEXT.DEFAULT;
  } catch {
    return AUTH_ERROR_TEXT.DEFAULT;
  }
}

export interface ResponseData {
  code: number;
  data: {
    token: string;
    refreshToken: string;
    user: {
      id: number;
    };
    jwtUserDetails: {
      sessionID: string;
    };
  };
}

/** Basic validation of authentication data required to initiate an auth session */
export function verifyAuthResponse(response: ResponseData): boolean {
  const isValidCode = response?.code === 200;
  const isValidBearerFormat = response?.data?.token.startsWith('Bearer ');
  const isValidRefreshTokenFormat = response?.data?.refreshToken.startsWith('Refresh ');
  const isValidUserId = Number.isInteger(response?.data?.user?.id);
  const isValidSessionID =
    typeof response?.data?.jwtUserDetails?.sessionID === 'string' &&
    !!response?.data?.jwtUserDetails?.sessionID.trim();

  const isResponseDataValid = [
    isValidCode,
    isValidBearerFormat,
    isValidRefreshTokenFormat,
    isValidUserId,
    isValidSessionID,
  ].every((item) => item);

  return isResponseDataValid;
}

export const checkIfMfaModeEnabled = (response: string): MfaMode => {
  try {
    const { errors } = JSON.parse(response);
    const firstError = errors?.[0];

    if (firstError?.errorType === ERROR_ACCESS_DENIED_INVALID_MFA_METHOD) {
      if (firstError?.errorArgs?.includes(VIRTUAL_MFA)) {
        return VIRTUAL_MFA;
      }
      if (firstError?.errorArgs?.includes(EMAIL_MFA)) {
        return EMAIL_MFA;
      }
    }
  } catch {
    return null;
  }

  return null;
};
